import root from 'window-or-global';
import {getMessage} from '../components/message/Message';
import {getLocale} from 'epic-common/src/locale'; // eslint-disable-line import/no-extraneous-dependencies
import {i18n, mediaTypes} from '../constants';

import {FileType} from '../models/Files';

const {supportedVideoTypes, supportedImageTypes} = mediaTypes;

export const roundToNearest = (num, nearest) => Math.round(num / nearest) * nearest;

export const getTimestamp = (_totalSeconds: number): string => {
    const formatHours = (hours: number): string | null => {
        if (!hours) return null;
        return hours.toString();
    };

    const formatMinutes = (minutes: number, hours: number): string => {
        if (hours && minutes < 10) return '0'.concat(minutes.toString());
        return minutes.toString();
    };

    const formatSeconds = (seconds: number): string => {
        if (seconds < 10) return '0'.concat(seconds.toString());
        return seconds.toString();
    };

    const totalSeconds: number = Math.round(_totalSeconds);
    const hours: number = Math.floor(totalSeconds / 3600);
    const minutes: number = Math.floor((totalSeconds % 3600) / 60);
    const seconds: number = Math.round(totalSeconds % 60);

    return [formatHours(hours), formatMinutes(minutes, hours), formatSeconds(seconds)]
        .filter(Boolean)
        .join(':');
};

const handlePluralTranslation = (messages, singularCode, pluralCode, value) =>
    getMessage(messages, value === 1 ? singularCode : pluralCode, [value]);

const HOUR_IN_SECONDS = 3600;
const DAY_IN_SECONDS = HOUR_IN_SECONDS * 24;
const MONTH_IN_SECONDS = DAY_IN_SECONDS * 30;
const YEAR_IN_SECONDS = DAY_IN_SECONDS * 365;

export const formatDuration = (totalSeconds, messages) => {
    if (!totalSeconds) return undefined;

    const seconds = Math.round(totalSeconds);

    const years = seconds / YEAR_IN_SECONDS;
    if (years >= 1) {
        return handlePluralTranslation(
            messages,
            'diesel.common.time.year',
            'diesel.common.time.years',
            Math.round(years)
        );
    }

    const months = seconds / MONTH_IN_SECONDS;
    if (months >= 1) {
        return handlePluralTranslation(
            messages,
            'diesel.common.time.month',
            'diesel.common.time.months',
            Math.round(months)
        );
    }

    const days = seconds / DAY_IN_SECONDS;
    if (days >= 1) {
        return handlePluralTranslation(
            messages,
            'diesel.common.time.day',
            'diesel.common.time.days',
            Math.round(days)
        );
    }

    const hours = seconds / HOUR_IN_SECONDS;
    if (hours >= 1) {
        return handlePluralTranslation(
            messages,
            'diesel.common.time.hour',
            'diesel.common.time.hours',
            Math.round(hours)
        );
    }

    const minutes = seconds / 60;
    if (minutes >= 1) {
        return handlePluralTranslation(
            messages,
            'diesel.common.time.minute',
            'diesel.common.time.minutes',
            Math.round(minutes)
        );
    }

    return handlePluralTranslation(
        messages,
        'diesel.common.time.second',
        'diesel.common.time.seconds',
        seconds
    );
};

export const formatRelativeTime = (isoString, messages) => {
    const diffMilli = new Date().valueOf() - new Date(isoString).valueOf();
    const diffSeconds = Math.floor(diffMilli / 1000);

    const duration = formatDuration(diffSeconds, messages);

    if (!duration) return getMessage(messages, 'diesel.common.time.just_now');

    return getMessage(messages, 'diesel.common.time.time_past', [duration]);
};

export const formatNumberCustom = (number, options = {}, customLocale?: string) => {
    const locale = customLocale || getLocale();
    const newOptions = {
        localeMatcher: 'best fit',
        maximumFractionDigits: 20,
        ...options
    };
    return new Intl.NumberFormat(locale, newOptions).format(number);
};

export const formatNumber = (number, numFixedDecimals = NaN, customLocale = undefined) => {
    const options = {
        maximumFractionDigits: 20,
        minimumFractionDigits: 0
    };

    if (!Number.isNaN(numFixedDecimals)) {
        options.minimumFractionDigits = numFixedDecimals;
        options.maximumFractionDigits = numFixedDecimals;
    }
    return formatNumberCustom(number, options, customLocale);
};

export const formatLeadingZero = (number, options = {}, customLocale = undefined) => {
    const locale = customLocale || getLocale();
    const newOptions = {
        minimumIntegerDigits: i18n.leadingZeroLocales.includes(locale) ? 2 : 1,
        ...options
    };
    return formatNumberCustom(number, newOptions, locale);
};

const SIZE_SUFFIX = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

export const prettyInstallSize = (bytes = 0) => {
    if (bytes === 0) return undefined;
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    if (i === 0) return `${bytes}${SIZE_SUFFIX[i]}`;
    const size = (bytes / 1024 ** i).toFixed(2);
    return `${size}${SIZE_SUFFIX[i]}`;
};

export const chunk = (array, size) => {
    const numChunks = Math.ceil(array.length / size);
    const chunks = new Array(numChunks);
    for (let i = 0; i < numChunks; i++) {
        chunks[i] = array.slice(size * i, size * (i + 1));
    }
    return chunks;
};

export const timestampExpiresAt = delta => {
    return Date.now() + delta;
};

export const hasTimestampExpired = expiredTimestamp => {
    return Date.now() - expiredTimestamp >= 0;
};

export const randomBetween = (min, max) => {
    return Math.random() * (max - min) + min;
};

/**
 * Used to prevent waves of server requests when an global event happens.
 * @see https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
 * @param {number} base - delay that this jitter is based on
 * @returns {number}
 */
export const jitter = base => {
    return Math.floor(randomBetween(base, base * 2));
};

export const formatLauncherDateTime = (dateTime: string = '') =>
    dateTime.replace(/^(\d*)\.(\d*)\.(\d*)-(\d*)\.(\d*)\.(\d*)(.*)?$/, '$1-$2-$3 $4:$5:$6Z');

export const formatTime = (time, options) => new Date(time).toLocaleString(getLocale(), options);

export const decodeHTMLEntities = stringWithEntities => {
    if (!root.document) return stringWithEntities;
    const txt = root.document.createElement('textarea'); // eslint-disable-line no-restricted-globals
    txt.innerHTML = stringWithEntities;
    return txt.value;
};

export const toScreamingSnakeCase = (value: string): string => {
    const slug: string = value
        .trim()
        .replace(/\s/, '-')
        .toLowerCase();

    return slug.replace('-', '_').toUpperCase();
};

export const toBoolean = (value: any): boolean => {
    if (!value) return false;
    return ['1', 'true'].includes(value.toString());
};

const getMimeTypeFromDataUrl = (url: string): RegExpMatchArray | null => {
    const dataUrlRegex = /(data:).*(?=;base64)/g;
    return url.match(dataUrlRegex);
};
const getFileExtensionFromUrl = (url: string): string | undefined => {
    const dataUrlType: RegExpMatchArray | null = getMimeTypeFromDataUrl(url);
    if (!!dataUrlType && dataUrlType.length) {
        return dataUrlType[0].replace('data:', '');
    }
    return url
        .toLowerCase()
        .split('.')
        .pop();
};
export const getFileTypeFromUrl = (url: string): FileType => {
    const extension: string = getFileExtensionFromUrl(url) || '';
    if (supportedImageTypes.includes(extension)) return FileType.IMAGE;
    if (supportedVideoTypes.includes(extension)) return FileType.VIDEO;
    return FileType.UNSUPPORTED;
};

export default {
    chunk,
    decodeHTMLEntities,
    formatDuration,
    formatLeadingZero,
    formatNumber,
    formatNumberCustom,
    formatRelativeTime,
    formatTime,
    getFileTypeFromUrl
};
